import { getSelectedCountryCode } from "@utils/countryUtils";
import { authenticatedFetch } from "@utils/authenticatedFetch";
import { requireApiOrigin } from "@utils/apiOrigin";
import { getCurrentUserId } from "@utils/userUtils";
import {
  getPreviousNetworkDataPeriod,
  NETWORK_REPORTING_EARLIEST_YEAR,
  reportingPeriodToApiParams,
  type NetworkReportingPeriod,
} from "@utils/networkReportingPeriod";
import { extractCmsSnapshotWarnings } from "@utils/networkCmsSnapshotWarnings";

export type { NetworkReportingPeriod };
export type NetworkDashboardRequestOptions = {
  reportingPeriod?: NetworkReportingPeriod;
  limit?: number;
  offset?: number;
  flag?: string;
  state?: string;
  sort?: string;
  direction?: "asc" | "desc";
};


export interface NetworkDashboardMeta {
  root_facility_id?: number;
  network_id?: number;
  root_facility_name?: string;
  ccns?: string[];
  facility_count?: number;
  release_suffix?: string | null;
  as_of_date?: string | null;
  /** CAH / some Aggregator panels use data_as_of instead of as_of_date. */
  data_as_of?: string | null;
  warnings?: string[];
  excluded_ccns?: string[];
  included_ccns?: string[];
  requested_ccns?: string[];
  data_quality_warnings?: string[];
  portfolio_facility_ids?: number[];
  release_id?: number | null;
  last_pipeline_refresh_date?: string | null;
  source_statuses?: Partial<Record<FqhcSourceProgram, FqhcSourceStatus>>;
  child_count?: number;
  is_parent_facility?: boolean;
  local_facilities?: Array<{
    facility_id: number;
    ccn?: string | null;
    name?: string | null;
    city?: string | null;
    state?: string | null;
    missing_sources?: string[];
  }>;
  /** CMS CCN → facility display name for portfolio members (rules API). */
  ccn_facility_names?: Record<string, string>;
  /** CMS CCN → Unicomply facility id for linked portfolio members (rules API). */
  ccn_facility_ids?: Record<string, number>;
  /** CMS CCN → street/city/state from linked facilities when CMS rows omit them. */
  ccn_facility_locations?: Record<
    string,
    {
      ccn?: string | null;
      provider_address?: string | null;
      address?: string | null;
      address_1?: string | null;
      city?: string | null;
      state?: string | null;
    }
  >;
}

export interface NetworkSummaryData {
  chain_name?: string | null;
  chain_id?: string | null;
  facility_count: number;
  portfolio_ccn_count?: number;
  snapshot_ccn_count?: number;
  total_beds: number;
  occupancy_rate?: number | null;
  penalty_exposure_36mo: number;
  penalty_exposure_delta_vs_prior_12mo?: number | null;
  sff_count: number;
  sff_facilities?: { ccn: string; provider_name?: string }[];
  network_avg_overall_rating?: number | null;
  state_avg_overall_rating?: number | null;
  vbp_estimated_impact_fy24?: number | null;
  vbp_facilities_with_reduction?: number;
  /** Reserved for a future in-app task feed; not shown in v1 network KPI strip. */
  action_items_count: number;
  action_items_urgent: number;
  /** Distinct US state codes for portfolio facilities (CMS snapshot). */
  portfolio_states?: string[];
}

export interface NetworkFlagCell {
  status: "ok" | "warn" | "alert" | "neutral";
  value?: string | number | null;
}

export interface NetworkRiskFlagsData {
  distribution: Record<string, number>;
  top_risk_facilities: {
    ccn: string;
    provider_name?: string;
    risk_score: number;
    primary_flags?: string[];
  }[];
  facilities: {
    ccn: string;
    provider_name?: string;
    provider_address?: string;
    city?: string;
    state?: string;
    beds?: number;
    risk_score: number;
    flags: Record<string, NetworkFlagCell>;
  }[];
  total_count: number;
}

export interface NetworkPanelResponse<T> {
  data: T;
  meta: NetworkDashboardMeta;
}

export type FqhcSourceState = "current" | "stale" | "failed" | "unavailable";

export type FqhcSourceProgram =
  | "hrsa_hc_sites"
  | "hrsa_uds_h80"
  | "hrsa_uds_lal"
  | "hrsa_chqr"
  | "hrsa_active_grants"
  | "hrsa_ftca"
  | "hrsa_340b";

export interface FqhcSourceStatus {
  status: FqhcSourceState;
  release_id: number | null;
  source_period: string | null;
  source_as_of_date: string | null;
  last_successful_ingest_at: string | null;
  last_attempted_at: string | null;
  row_counts: Record<string, number> | null;
  error_summary: string | null;
}

export interface FqhcGrantsExpiringKpi {
  count: number;
  eligible_centers: number;
  earliest_expires_on: string | null;
  missing_date_count: number;
}

export interface FqhcCqmDeltaKpi {
  points: number | null;
  reporting_year: number | null;
  measure_count: number;
  contributing_centers: number;
}

export interface FqhcChqrKpi {
  badge_count: number | null;
  cycle_year: number | null;
  centers_with_badges: number;
  centers_in_scope: number;
}

export interface FqhcFtcaKpi {
  coverage_pct: number | null;
  deemed_count: number;
  eligible_centers: number;
  lapsed_count: number;
  earliest_expires_on: string | null;
}

export interface FqhcDashboardKpis {
  grants_expiring_12m: FqhcGrantsExpiringKpi;
  cqm_delta: FqhcCqmDeltaKpi;
  chqr: FqhcChqrKpi;
  ftca: FqhcFtcaKpi;
}

export type FqhcIdentityStatus = "matched" | "unmatched" | "ambiguous";
export type FqhcCenterType = "awardee" | "look_alike";
export type FqhcFtcaStatus =
  | "deemed"
  | "lapsed"
  | "not_found"
  | "not_eligible";
export type FqhcFtcaStatusReason =
  | "look_alike"
  | "source_unavailable"
  | "period_not_started"
  | "status_unavailable";

export interface FqhcDashboardFacility {
  facility_id: number | null;
  ccn: string | null;
  health_center_key: string | null;
  bhcmis_id: string | null;
  identity_status: FqhcIdentityStatus;
  name: string | null;
  city: string | null;
  state: string | null;
  center_type: FqhcCenterType | null;
  grant_or_designation_expires_on: string | null;
  expiration_availability_reason: string | null;
  chqr_badge_count: number | null;
  chqr_cycle_year: number | null;
  cqm_delta_points: number | null;
  cqm_reporting_year: number | null;
  cqm_measure_count: number | null;
  ftca_status: FqhcFtcaStatus | null;
  ftca_status_reason: FqhcFtcaStatusReason | null;
  ftca_expires_on: string | null;
  is_340b_registered: boolean | null;
  contract_pharmacy_count: number | null;
  active_site_count: number | null;
  missing_sources: string[];
}

export interface FqhcDashboardData {
  kpis: FqhcDashboardKpis;
  facilities: FqhcDashboardFacility[];
  total_count: number;
}

export interface SnfDashboardSummary {
  facility_count: number | null;
  average_star_rating: number | null;
  rated_facility_count: number | null;
  lowest_star_rating: number | null;
  below_3_hprd_count: number | null;
  hprd_facility_count: number | null;
  active_f_tag_count: number | null;
  ij_count: number | null;
  severity_d_plus_count: number | null;
  severity_a_c_count: number | null;
  estimated_vbp_adjustment_ytd_usd: number | null;
  vbp_contributing_facility_count: number | null;
  federal_fiscal_year: number | null;
}

export interface SnfDashboardFacility {
  facility_id?: number | null;
  ccn: string | null;
  name: string | null;
  city: string | null;
  state: string | null;
  overall_star_rating: number | null;
  pbj_quarter: string | null;
  hprd: number | null;
  active_f_tag_count: number | null;
  ij_count: number | null;
  survey_due_date: string | null;
  days_until_survey_due: number | null;
  estimated_adjustment_ytd_usd: number | null;
  [key: string]: unknown;
}

export interface SnfDashboardData {
  summary: SnfDashboardSummary;
  facilities: SnfDashboardFacility[];
  total_count: number;
}

function snfSummaryFromParts(
  summary: Partial<SnfDashboardSummary> | null | undefined,
): SnfDashboardSummary {
  return {
    facility_count: summary?.facility_count ?? null,
    average_star_rating: summary?.average_star_rating ?? null,
    rated_facility_count: summary?.rated_facility_count ?? null,
    lowest_star_rating: summary?.lowest_star_rating ?? null,
    below_3_hprd_count: summary?.below_3_hprd_count ?? null,
    hprd_facility_count: summary?.hprd_facility_count ?? null,
    active_f_tag_count: summary?.active_f_tag_count ?? null,
    ij_count: summary?.ij_count ?? null,
    severity_d_plus_count: summary?.severity_d_plus_count ?? null,
    severity_a_c_count: summary?.severity_a_c_count ?? null,
    estimated_vbp_adjustment_ytd_usd: summary?.estimated_vbp_adjustment_ytd_usd ?? null,
    vbp_contributing_facility_count: summary?.vbp_contributing_facility_count ?? null,
    federal_fiscal_year: summary?.federal_fiscal_year ?? null,
  };
}

/** Rules omits summary when the portfolio has no CMS CCN. Always return a summary object. */
export function normalizeSnfDashboardData(
  data: Partial<SnfDashboardData> | null | undefined,
): SnfDashboardData {
  const facilities = data?.facilities;
  const totalCount = data?.total_count;
  return {
    summary: snfSummaryFromParts(data?.summary),
    facilities: Array.isArray(facilities) ? facilities : [],
    total_count: typeof totalCount === "number" ? totalCount : 0,
  };
}

export type RhcFilingStatus =
  | "FILED"
  | "DUE_SOON"
  | "PENDING"
  | "OVERDUE"
  | "UNKNOWN";

export type RhcAirStatus = "RECONCILED" | "PENDING_SETTLEMENT" | "UNKNOWN";

export type RhcRuralDesignationStatus =
  | "ACTIVE"
  | "GRANDFATHERED"
  | "NONE"
  | "UNKNOWN";

export interface RhcDashboardSummary {
  facility_count: number | null;
  filed_count: number | null;
  pending_count: number | null;
  overdue_count: number | null;
  due_soon_count: number | null;
  average_visits_per_clinical_fte: number | null;
  visits_per_clinical_fte_coverage_count: number | null;
  productivity_standard_applicable_count: number | null;
  productivity_standard_met_count: number | null;
  reconciled_air_count: number | null;
  pending_air_count: number | null;
  active_rural_designation_count: number | null;
  grandfathered_rural_designation_count: number | null;
  unknown_rural_designation_count: number | null;
}

export interface RhcDashboardFacility {
  facility_id?: number | null;
  ccn: string | null;
  name: string | null;
  city: string | null;
  state: string | null;
  provider_based: boolean | null;
  filing_status: RhcFilingStatus | null;
  next_filing_date: string | null;
  visits_per_clinical_fte: number | null;
  productivity_standard_applicable: boolean | null;
  productivity_standard_met: boolean | null;
  productivity_standard_capacity: number | null;
  air_status: RhcAirStatus | null;
  rural_designation_status: RhcRuralDesignationStatus | null;
  missing_sources: string[];
  [key: string]: unknown;
}

export interface RhcProviderBasedFacility {
  facility_id?: number | null;
  ccn: string | null;
  name: string | null;
  city: string | null;
  state: string | null;
  parent_ccn: string | null;
  parent_facility_name: string | null;
  parent_bed_count: number | null;
  air_cap_exposed: boolean | null;
  air_cap_basis?: string | null;
  cost_per_visit: number | null;
  provider_based_effective_date: string | null;
  [key: string]: unknown;
}

export interface RhcDashboardData {
  summary: RhcDashboardSummary;
  facilities: RhcDashboardFacility[];
  provider_based_facilities: RhcProviderBasedFacility[];
  total_count: number;
}

export type RhcDashboardSort =
  | "name"
  | "state"
  | "last_full_survey_date"
  | "cost_per_visit"
  | "provider_based";

export interface CahDashboardSummary {
  cah_count: number | null;
  public_mbqip_available_count: number | null;
  public_mbqip_domain_count: number | null;
  weighted_network_operating_margin: string | number | null;
}

export interface CahDashboardFacility {
  facility_id?: number | null;
  ccn: string | null;
  facility_name?: string | null;
  name?: string | null;
  city: string | null;
  state: string | null;
  provider_address?: string | null;
  certified_bed_count: number | null;
  swing_bed_adc: number | null;
  operating_margin: number | string | null;
  last_standard_survey_date: string | null;
  risk?: number | string | null;
  [key: string]: unknown;
}

export interface CahDashboardData {
  summary: CahDashboardSummary;
  facilities: CahDashboardFacility[];
  total_count: number;
}

export type CahDashboardSort =
  | "risk"
  | "name"
  | "state"
  | "last_standard_survey_date"
  | "operating_margin"
  | "swing_bed_adc"
  | "certified_bed_count";

export type FqhcDashboardSort =
  | "grant_expiration"
  | "cqm_delta"
  | "chqr_badges"
  | "ftca_status"
  | "sites"
  | "name";

export type NetworkSummaryFetcher = (
  options: NetworkDashboardRequestOptions,
) => Promise<NetworkPanelResponse<NetworkSummaryData>>;

export interface NetworkSummaryPeriodSearchResult {
  response: NetworkPanelResponse<NetworkSummaryData> | null;
  reportingPeriod: NetworkReportingPeriod | null;
  hasData: boolean;
}

/** Distinguish an absent CMS snapshot from valid data whose metrics happen to be zero. */
export function networkSummaryHasCmsData(
  response: NetworkPanelResponse<NetworkSummaryData>,
): boolean {
  if (response.meta.release_id === null) return false;
  if (response.data.snapshot_ccn_count != null) {
    return response.data.snapshot_ccn_count > 0;
  }
  return response.data.facility_count > 0;
}

/** Probe reporting periods backward until CMS snapshot data exists or history is exhausted. */
export async function findNetworkSummaryWithData(
  fetchSummary: NetworkSummaryFetcher,
  startingPeriod: NetworkReportingPeriod,
  earliestYear = NETWORK_REPORTING_EARLIEST_YEAR,
): Promise<NetworkSummaryPeriodSearchResult> {
  let period: NetworkReportingPeriod | null = startingPeriod;
  let lastResponse: NetworkPanelResponse<NetworkSummaryData> | null = null;

  while (period) {
    const response = await fetchSummary({ reportingPeriod: period });
    lastResponse = response;
    if (networkSummaryHasCmsData(response)) {
      return { response, reportingPeriod: period, hasData: true };
    }
    period = getPreviousNetworkDataPeriod(period, earliestYear);
  }

  return { response: lastResponse, reportingPeriod: null, hasData: false };
}

function mergeReportingPeriodParams(
  extra: Record<string, string | number | undefined> | undefined,
  options?: NetworkDashboardRequestOptions,
): Record<string, string | number | undefined> | undefined {
  if (!options?.reportingPeriod) return extra;
  const periodParams = reportingPeriodToApiParams(options.reportingPeriod);
  return { ...extra, ...periodParams };
}

function mergeRequestOptions(
  extra: Record<string, string | number | undefined> | undefined,
  options?: NetworkDashboardRequestOptions,
): Record<string, string | number | undefined> | undefined {
  const merged = { ...(mergeReportingPeriodParams(extra, options) ?? {}) };
  if (options?.limit != null) merged.limit = options.limit;
  if (options?.offset != null) merged.offset = options.offset;
  if (options?.flag) merged.flag = options.flag;
  if (options?.state) merged.state = options.state;
  if (options?.sort) merged.sort = options.sort;
  if (options?.direction) merged.direction = options.direction;
  return Object.keys(merged).length ? merged : undefined;
}

async function fetchNetworkPanel<T>(
  panel: string,
  root: { facilityId?: number; networkId?: number },
  extra?: Record<string, string | number | undefined>,
  options?: NetworkDashboardRequestOptions,
): Promise<NetworkPanelResponse<T>> {
  const mergedExtra = mergeRequestOptions(extra, options);
  const userId = getCurrentUserId();
  const country = getSelectedCountryCode() || "USA";
  const params = new URLSearchParams({
    user_id: String(userId),
    country_code: country,
  });
  if (root.networkId != null) {
    params.set("network_id", String(root.networkId));
  } else if (root.facilityId != null) {
    params.set("facility_id", String(root.facilityId));
  }
  if (mergedExtra) {
    Object.entries(mergedExtra).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        params.set(k, String(v));
      }
    });
  }
  const url = `${requireApiOrigin()}/api/v1/network-dashboard/${panel}?${params.toString()}`;
  const res = await authenticatedFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg =
      (typeof err?.detail === "string" && err.detail) ||
      (typeof err?.message === "string" && err.message) ||
      `Network dashboard request failed (${res.status})`;
    throw new Error(msg);
  }
  const json = await res.json();
  return {
    data: (json.data ?? json.result?.data ?? {}) as T,
    meta: (json.meta ?? json.result?.meta ?? {}) as NetworkDashboardMeta,
  };
}

export function getNetworkSummary(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<NetworkSummaryData>("summary", { facilityId }, undefined, options);
}

export function getNetworkSummaryForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<NetworkSummaryData>("summary", { networkId }, undefined, options);
}

export function getNetworkFqhc(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<FqhcDashboardData>("fqhc", { facilityId }, undefined, options);
}

export function getNetworkFqhcForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<FqhcDashboardData>("fqhc", { networkId }, undefined, options);
}

export async function getNetworkSnf(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  const response = await fetchNetworkPanel<SnfDashboardData>(
    "snf",
    { facilityId },
    undefined,
    options,
  );
  return { ...response, data: normalizeSnfDashboardData(response.data) };
}

export async function getNetworkSnfForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  const response = await fetchNetworkPanel<SnfDashboardData>(
    "snf",
    { networkId },
    undefined,
    options,
  );
  return { ...response, data: normalizeSnfDashboardData(response.data) };
}

export function getNetworkRhc(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<RhcDashboardData>("rhc", { facilityId }, undefined, options);
}

export function getNetworkRhcForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<RhcDashboardData>("rhc", { networkId }, undefined, options);
}

/**
 * CAH Rules contract is top-level `{ meta, summary, facilities }` (not `{ data, meta }`).
 * Normalize into the shared NetworkPanelResponse shape for hub consumers.
 */
async function fetchNetworkCahPanel(
  root: { facilityId?: number; networkId?: number },
  options?: NetworkDashboardRequestOptions,
): Promise<NetworkPanelResponse<CahDashboardData>> {
  const mergedExtra = mergeRequestOptions(undefined, options);
  const userId = getCurrentUserId();
  const country = getSelectedCountryCode() || "USA";
  const params = new URLSearchParams({
    user_id: String(userId),
    country_code: country,
  });
  if (root.networkId != null) {
    params.set("network_id", String(root.networkId));
  } else if (root.facilityId != null) {
    params.set("facility_id", String(root.facilityId));
  }
  if (mergedExtra) {
    Object.entries(mergedExtra).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        params.set(k, String(v));
      }
    });
  }
  const url = `${requireApiOrigin()}/api/v1/network-dashboard/type/cah?${params.toString()}`;
  const res = await authenticatedFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg =
      (typeof err?.detail === "string" && err.detail) ||
      (typeof err?.message === "string" && err.message) ||
      `Network dashboard request failed (${res.status})`;
    throw new Error(msg);
  }
  const json = await res.json();
  const nested =
    (json.data as Record<string, unknown> | undefined) ??
    (json.result?.data as Record<string, unknown> | undefined) ??
    {};
  const summary = (json.summary ??
    nested.summary ??
    {}) as CahDashboardSummary;
  const facilities = (json.facilities ??
    nested.facilities ??
    []) as CahDashboardFacility[];
  const totalFromNested =
    typeof nested.total_count === "number" ? nested.total_count : undefined;
  const total_count =
    totalFromNested ??
    (typeof summary.cah_count === "number" ? summary.cah_count : facilities.length);
  return {
    data: { summary, facilities, total_count },
    meta: (json.meta ?? json.result?.meta ?? {}) as NetworkDashboardMeta,
  };
}

export function getNetworkCah(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkCahPanel({ facilityId }, options);
}

export function getNetworkCahForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkCahPanel({ networkId }, options);
}

export function getNetworkRiskFlags(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<NetworkRiskFlagsData>("risk-flags", { facilityId }, undefined, options);
}

export function getNetworkRiskFlagsForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<NetworkRiskFlagsData>("risk-flags", { networkId }, undefined, options);
}

export function getNetworkRatings(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>("ratings", { facilityId }, undefined, options);
}

export function getNetworkRatingsForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>("ratings", { networkId }, undefined, options);
}

export function getNetworkSurveyDrivers(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>(
    "survey-drivers",
    { facilityId },
    undefined,
    options,
  );
}

export function getNetworkSurveyDriversForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>(
    "survey-drivers",
    { networkId },
    undefined,
    options,
  );
}

export function getNetworkStaffing(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>("staffing", { facilityId }, undefined, options);
}

export function getNetworkStaffingForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>("staffing", { networkId }, undefined, options);
}

export function getNetworkQualityMeasures(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>(
    "quality-measures",
    { facilityId },
    undefined,
    options,
  );
}

export function getNetworkQualityMeasuresForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>(
    "quality-measures",
    { networkId },
    undefined,
    options,
  );
}

export function getNetworkVbp(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>("vbp", { facilityId }, undefined, options);
}

export function getNetworkVbpForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<Record<string, unknown>>("vbp", { networkId }, undefined, options);
}

export type NetworkDashboardView = "operational" | "executive";

export interface NetworkExecutiveSummaryData {
  penalty_history: {
    total_cmp_and_denial_paid: number;
    facilities_with_penalties_count: number;
    facility_count: number;
    breakdown: {
      cmp_penalties: number;
      vbp_adjustments?: number | null;
      denied_payment_days: number;
    };
    trend_callout: { direction?: string; multiplier?: number | null; text: string };
  };
  forward_estimate: {
    total_12mo: number;
    breakdown: {
      high_confidence: number;
      modeled_tail: number;
      facilities_driving_80_pct: number;
    };
    trend_callout: { direction?: string; text: string };
  };
  glance: {
    facility_count: number;
    total_beds: number;
    states_count: number;
    states: string[];
    network_avg_overall_rating?: number | null;
    national_avg_overall_rating?: number | null;
    sff_flagged_count: number;
    sff_context?: string | null;
    fy24_vbp_impact?: number | null;
    vbp_facilities_under_1x?: number;
  };
  highest_risk_facilities: {
    rank: number;
    ccn: string;
    provider_name?: string;
    city?: string;
    state?: string;
    estimate_source: "unicomply" | "cms_history";
    enforcement_status: string;
    enforcement_tier: string;
    primary_driver: string;
    forward_estimate_12mo: number;
  }[];
  unicomply_coverage: {
    ftags_monitored_count: number;
    ftags_total_in_citation_history: number;
    ftags_monitored_pct: number;
    top_tags: string[];
    forward_estimate_tracked: number;
    forward_estimate_total: number;
    facilities_with_active_tracking?: number;
  };
}

export function getNetworkExecutiveSummary(
  facilityId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<NetworkExecutiveSummaryData>(
    "executive-summary",
    { facilityId },
    undefined,
    options,
  );
}

export function getNetworkExecutiveSummaryForNetwork(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
) {
  return fetchNetworkPanel<NetworkExecutiveSummaryData>(
    "executive-summary",
    { networkId },
    undefined,
    options,
  );
}

/** CMS snapshot gap warnings for a network portfolio (for import/setup UI). */
export async function fetchNetworkCmsSnapshotWarnings(
  networkId: number,
  options?: NetworkDashboardRequestOptions,
): Promise<string[]> {
  try {
    const res = await getNetworkSummaryForNetwork(networkId, options);
    return extractCmsSnapshotWarnings(res.meta?.data_quality_warnings);
  } catch {
    return [];
  }
}

export {
  formatNetworkCurrency,
  formatOccupancy,
  collectPortfolioStatesFromMeta,
  formatNetworkPortfolioCcnSuffix,
  formatNetworkPortfolioPeriodEnding,
  formatNetworkPortfolioSelectorDetail,
  formatNetworkPortfolioSubtitle,
  formatNetworkStateList,
  isNetworkPortfolioCcn,
  normalizeNetworkPortfolioCcn,
  normalizeNetworkPortfolioStates,
  resolveNetworkPortfolioCcns,
} from "./networkDashboardFormat";
